import React, { useState, useEffect } from 'react';
import {
    Tooltip,
    IconButton,
    Grid,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    TablePagination,
    InputLabel,
    FormControl,
    FormHelperText,
    MenuItem,
    Select
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Helmet from 'react-helmet';
import { DataFilter, DataTable, PagedTable, TablePaginationActions, BulkActionIconButton, BulkActionWrapper } from '../../../common';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import {
    Portlet,
    PortletBody,
    PortletHeader,
    PortletHeaderToolbar,
} from "../../../partials/content/Portlet";
import { infoToastify,errorToastify } from '../../../util/toast';

const DEFAULT_SORT = [{ id: 'code', direction: 'asc' }];
const SHOW_SEARCH_FILTERS = true;

const useStyles = makeStyles((theme) => ({
    root: {
        margin: 'auto',
    },
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },

    icon: {
        fontSize: 17,
        marginTop: 1,
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogContent: {
        padding: '0px',
    },
    formControl: {
        paddingRight: theme.spacing(1),
        width: '60%',
        marginRight:"20px",
        marginTop:"-10px"
    },
    masterLanding:{
        height: 'calc(100vh - 350px)',
        minHeight: '300px',
        overflowY: 'auto',
        "& th": {
            top:'0px'
        }
    }
    
}));

export default function MasterImport({
    intl, lookupDialogOpen, hospitalServicesMasterData, hospitalServicesMasterFields, 
    userRole, order, filter, sort, handleRefresh, codeType,
    facilities, selectedItems, importHospitalServicesPending,
    actions: {
        hospitalServicesLookupFromMaster,
        searchGetByName,
        searchReset,
        resetPagedTable,
        setList,
        setSelectedItems,
        setHopsitalServicesLookupDialog,
        importHospitalServices,
        importAllHospitalServices
    }
}) {
    const [facilityError, setFacilityError] = useState(false);
    const [facilityValue, setFacilityValue] = useState('');
    const [refresh, setRefresh] = useState(Promise.resolve());
    const [currentGridState, setCurrentGridState] = useState({ filter: null, sort: DEFAULT_SORT });

    const classes = useStyles();

    const getActiveCodes = (codes, records) => codes.filter(c => !records.some(r => r.code === c && r.isDiscontinued));

    const handleBulkImport = (processAll, selectedIds) => {
        if (!facilityValue) {
            setFacilityError(true);
            return;
        }
        if (processAll) {
            // All records: use dedicated API with filter/sort context
            const payload = {
                facilityId: facilityValue,
                codeType: codeType,
                inputRequest: {
                    filter: currentGridState.filter || null,
                    sort: currentGridState.sort || [],
                    page: null
                }
            };
            importAllHospitalServices(payload, () => {
                handleLookupDialogClose();
                infoToastify(`${codeType} code import is in progress. You will be notified once it is complete.`);
                
            });
        } else {
            // Selected records only: use existing API with explicit codes list
            const payload = {
                codes:getActiveCodes(selectedIds, hospitalServicesMasterData),
                facilityId: facilityValue,
                codeType: codeType,
            };
            if(payload.codes.length <=0)
            {
                errorToastify(`Selected ${codeType} codes are Discontinued and cannot be imported`);
                return
            }

            importHospitalServices(payload, () => {
                handleLookupDialogClose();
                infoToastify(`${codeType} code import is in progress. You will be notified once it is complete.`);
                
            });
        }
    };

    function handleLookupDialogClose() {
        setFacilityValue('');
        setHopsitalServicesLookupDialog(null);
        handleRefresh();
    }

    function handleLoad(sort, filter, pagination) {
        setCurrentGridState({ filter, sort });
        if(lookupDialogOpen && facilityValue && codeType) {
            hospitalServicesLookupFromMaster(pagination, sort, filter, codeType, facilityValue);
        }
    }

    function handleMasterRefresh() {
        if(lookupDialogOpen && facilityValue && codeType) {
            setList([]);
            setRefresh(Promise.resolve());
        }
    }

    useEffect(() => {
        if(lookupDialogOpen && facilityValue && codeType) {
            const pageName = {
                name: 'hospitalservices-master-landing'
            }
            searchGetByName(pageName);
            return function cleanup() {
                searchReset();
                resetPagedTable(pageName.name);
                setSelectedItems([]);
            }
        }
    }, [searchGetByName, lookupDialogOpen, facilityValue, codeType]);

    return (<>
        <Dialog fullWidth maxWidth='lg' open={lookupDialogOpen} onClose={handleLookupDialogClose} aria-labelledby="form-dialog-title">
        <DialogContent dividers style={{padding: '0px'}}>
            <Portlet>
                <PortletHeader
                    title="Add Hospital Services"
                    handleRefresh={handleMasterRefresh}
                    showSearchFilters={SHOW_SEARCH_FILTERS}
                    showSearchFiltersDropdown={false}
                    includeAdvanceFilters={true}
                    name="hospitalservices-master-landing"
                    toolbar={
                        <PortletHeaderToolbar fullWidth>
                            <Grid container spacing={3} justifyContent="flex-start">
                                <Grid item md={6}><h4 className='mt-2'>{`Add ${codeType} Codes`}</h4></Grid>
                                <Grid item md={6} className='mt-2' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <FormControl className={classes.formControl} error={facilityError}>
                                        <InputLabel htmlFor="facility">Select Facility</InputLabel>
                                        <Select
                                            value={facilityValue}
                                            onChange={(event) => {
                                                setFacilityError(false)
                                                setFacilityValue(event.target.value)}}
                                            inputProps={{
                                                name: "facility",
                                                id: "facility"
                                            }}
                                        >
                                            {facilities.map(({ id, name }) => (
                                                <MenuItem
                                                    key={name}
                                                    value={id}
                                                >
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {facilityError && (
                                            <FormHelperText error>Please select a facility</FormHelperText>
                                        )}
                                    </FormControl>
                            
                                </Grid>
                            </Grid>
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody >
                    <div className="row">
                        <div className={`col-md-12 ${classes.masterLanding}`}>
                            <PagedTable
                                name="hospitalservices-master-landing"
                                fields={hospitalServicesMasterFields}
                                data={hospitalServicesMasterData}
                                onLoad={handleLoad}
                                refresh={refresh}
                                defaultSort={DEFAULT_SORT}
                                hasCheckbox={true}
                                showSelectAll={true}
                                checkBoxSelectedFieldName={`code`}
                                shouldRenderLazy={true}
                                title="Hospital Service Master"
                                criteriasSaveEnabled={true}
                                showSearchFilters={SHOW_SEARCH_FILTERS}
                                stickyHeader={true}
                            />
                        </div>
                    </div>
                </PortletBody>
            </Portlet>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleLookupDialogClose} variant='contained'>
                Cancel
            </Button>
            <BulkActionWrapper
                pageName="hospitalservices-master-landing"
                infoMessage="(Discontinued Records will be skipped)"
                actionLabel="Import"
                onConfirm={handleBulkImport}
                title={`Import ${codeType} Codes`}
                customText={`Are you sure you want to import selected ${codeType} Codes? It will impacted on all shoppables and negotiated rates?`}
                isPending={importHospitalServicesPending}
            >
                {(onClick) => (
                    <Button
                        onClick={onClick}
                        variant='contained'
                        color="primary"
                        disabled={importHospitalServicesPending || selectedItems.length === 0}
                    >
                        {importHospitalServicesPending ? 'Importing...' : 'Import'}
                    </Button>
                )}
            </BulkActionWrapper>
        </DialogActions>
        </Dialog>
    </>);
}